import { useQuery } from '@tanstack/react-query'

export type InsightsRange = '24h' | '7d' | '30d'

export interface Insights {
  range: InsightsRange
  rangeStart: number
  rangeEnd: number
  overview: {
    totalSearches: number
    zeroResultCount: number
    zeroResultRate: number
    clickThroughRate: number
    avgLatencyMs: number
    uniqueSessions: number
    trend: {
      searches: number
      zeroResultRate: number
      ctr: number
    }
  }
  topQueries: Array<{
    query: string
    searches: number
    clicks: number
    ctr: number
    avgResults: number
    trend: number | null
  }>
  zeroResults: Array<{
    query: string
    count: number
    lastSeen: number
    hasSynonym: boolean
    hasRedirect: boolean
  }>
  brandShare: Array<{ brand: string; clicks: number; percentage: number }>
  categoryShare: Array<{ category: string; clicks: number; percentage: number }>
  timeSeries: Array<{
    date: string
    searches: number
    clicks: number
    zeroResults: number
  }>
  hourlyHeatmap: number[]
  topClickedProducts: Array<{
    productId: string
    title: string
    imageUrl: string | null
    brand: string | null
    clicks: number
  }>
}

export function useInsights(range: InsightsRange) {
  return useQuery({
    queryKey: ['insights', range],
    queryFn: async (): Promise<Insights> => {
      const response = await fetch(`/api/stats/insights?range=${range}`, {
        credentials: 'include',
      })
      if (!response.ok) throw new Error('Failed to fetch insights')
      return response.json()
    },
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  })
}
